/* ==========================================================================
   SHOESLI V5 — behaviour
   Small, dependency-free modules. Everything is transform/opacity driven,
   interruptible, and respects prefers-reduced-motion.
   ========================================================================== */
(function () {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Arm the scroll-reveal styles. Without this class the content is simply
  // visible, so a script error can never leave the page blank.
  document.documentElement.classList.add('v5-js');
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------------------------------------------------------------------
     Scroll lock. Preserves scroll position and compensates for the
     scrollbar so the page never shifts sideways when a drawer opens.
     --------------------------------------------------------------------- */
  let lockCount = 0;
  let lockedY = 0;
  function lockScroll() {
    if (lockCount++ > 0) return;
    lockedY = window.scrollY;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.position = 'fixed';
    document.body.style.insetBlockStart = `-${lockedY}px`;
    document.body.style.inlineSize = '100%';
    if (gap > 0) document.body.style.paddingInlineEnd = `${gap}px`;
  }
  function unlockScroll() {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.style.position = '';
    document.body.style.insetBlockStart = '';
    document.body.style.inlineSize = '';
    document.body.style.paddingInlineEnd = '';
    window.scrollTo(0, lockedY);
  }

  /* ---------------------------------------------------------------------
     Focus trap
     --------------------------------------------------------------------- */
  function trapFocus(container, onEscape, restoreTo) {
    const previous = restoreTo || document.activeElement;
    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener('keydown', onKey);
    return function release() {
      container.removeEventListener('keydown', onKey);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }

  /* ---------------------------------------------------------------------
     Drawer controller — shared by menu, cart and filters.
     --------------------------------------------------------------------- */
  class Drawer {
    constructor(panel, overlay) {
      this.panel = panel;
      this.overlay = overlay;
      this.release = null;
      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      if (overlay) overlay.addEventListener('click', this.close);
    }
    get isOpen() {
      return this.panel.classList.contains('is-open');
    }
    /* Tab is trapped inside the panel, but the page behind a modal drawer was
       still readable in a screen reader's browse mode and still hoverable.
       `inert` takes the rest of the document out of reach for the duration.
       The filter sheet is excluded: on desktop it is an inline panel, not a
       modal, and inerting the page around it would be wrong. */
    get isModal() {
      return this.panel.id !== 'V5FilterDrawer';
    }
    hideRest() {
      if (!this.isModal) return;
      const top = this.panel.closest('body > *') || this.panel;
      this.inerted = Array.prototype.filter.call(document.body.children, (el) => {
        return el !== top && el !== this.overlay && !el.contains(this.panel) && !el.hasAttribute('inert');
      });
      this.inerted.forEach((el) => el.setAttribute('inert', ''));
    }
    showRest() {
      if (!this.inerted) return;
      this.inerted.forEach((el) => el.removeAttribute('inert'));
      this.inerted = null;
    }
    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || null;
      this.panel.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-open');
      this.panel.removeAttribute('inert');
      this.hideRest();
      lockScroll();
      // Restore focus to the control that opened the drawer, not merely to
      // whatever happened to be focused (a mouse click leaves that on <body>).
      this.release = trapFocus(this.panel, this.close, this.trigger);
      // Focus after the transition starts so the browser does not scroll
      // the offscreen panel into view.
      // The panel is visibility:hidden until `.is-open` lands, and a hidden
      // element refuses focus. Flush style/layout synchronously so the new
      // visibility is in effect before we ask for focus.
      const focusTarget = () => this.panel.querySelector('[data-autofocus]') || this.panel;
      void this.panel.offsetHeight;
      focusTarget().focus({ preventScroll: true });
      if (!this.panel.contains(document.activeElement)) {
        requestAnimationFrame(() => focusTarget().focus({ preventScroll: true }));
      }
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      document.dispatchEvent(new CustomEvent('v5:drawer:open', { detail: this.panel }));
    }
    close() {
      if (!this.isOpen) return;
      this.panel.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-open');
      // Put the panel back out of reach of keyboard, pointer and AT.
      if (this.panel.id !== 'V5FilterDrawer') this.panel.setAttribute('inert', '');
      this.showRest();
      unlockScroll();
      if (this.release) {
        this.release();
        this.release = null;
      }
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      document.dispatchEvent(new CustomEvent('v5:drawer:close', { detail: this.panel }));
    }
  }
  window.V5Drawer = Drawer;

  /* ---------------------------------------------------------------------
     Header: stuck shadow + mega panels + mobile menu
     --------------------------------------------------------------------- */
  class V5Header extends HTMLElement {
    connectedCallback() {
      this.setupStuck();
      this.setupPanels();
    }

    setupStuck() {
      const group = this.closest('.shopify-section-group-header-group') || this.parentElement;
      const root = document.documentElement;
      let lastY = window.scrollY;
      let ticking = false;

      const update = () => {
        ticking = false;
        const y = window.scrollY;
        const scrolled = y > 24;
        this.classList.toggle('is-stuck', scrolled);
        root.classList.toggle('is-scrolled', scrolled);

        // Ignore rubber-band overscroll and sub-pixel jitter.
        const delta = y - lastY;
        if (y > 240 && delta > 6) {
          group.classList.add('is-hidden');
        } else if (delta < -6 || y <= 240) {
          group.classList.remove('is-hidden');
        }
        lastY = y;
      };

      window.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        },
        { passive: true }
      );

      // A drawer must never leave the header hidden behind it.
      document.addEventListener('v5:drawer:open', () => group.classList.remove('is-hidden'));
      update();
    }

    setupPanels() {
      this.openItem = null;
      const triggers = this.querySelectorAll('[data-v5-panel-trigger]');
      if (!triggers.length) return;

      triggers.forEach((trigger) => {
        const item = trigger.closest('.v5-nav__item');
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;

        const open = () => this.openPanel(item, trigger, panel);
        const close = () => this.closePanel(item, trigger, panel);

        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          item.classList.contains('is-open') ? close() : open();
        });
        // Pointer users get hover; the click handler keeps it keyboard-usable.
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          item.addEventListener('mouseenter', open);
          item.addEventListener('mouseleave', close);
        }
        trigger.addEventListener('focus', open);
        panel.addEventListener('focusout', (e) => {
          if (!panel.contains(e.relatedTarget) && !item.contains(e.relatedTarget)) close();
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.openItem) {
          const { item, trigger, panel } = this.openItem;
          this.closePanel(item, trigger, panel);
          trigger.focus();
        }
      });
    }

    openPanel(item, trigger, panel) {
      if (this.openItem && this.openItem.item !== item) {
        const p = this.openItem;
        this.closePanel(p.item, p.trigger, p.panel);
      }
      panel.hidden = false;
      // Force a frame so the transition runs from the hidden state.
      requestAnimationFrame(() => panel.classList.add('is-open'));
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      this.openItem = { item, trigger, panel };
    }

    closePanel(item, trigger, panel) {
      panel.classList.remove('is-open');
      item.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      if (this.openItem && this.openItem.item === item) this.openItem = null;
      const done = () => {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      };
      if (reduced.matches) done();
      else setTimeout(done, 220);
    }
  }
  if (!customElements.get('v5-header')) customElements.define('v5-header', V5Header);

  /* ---------------------------------------------------------------------
     Mobile menu drawer wiring
     --------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    const panel = document.getElementById('V5MenuDrawer');
    const overlay = document.querySelector('[data-v5-menu-overlay]');
    if (!panel) return;
    const drawer = new Drawer(panel, overlay);
    document.querySelectorAll('[data-v5-menu-open]').forEach((btn) =>
      btn.addEventListener('click', () => drawer.open(btn))
    );
    document.querySelectorAll('[data-v5-menu-close]').forEach((btn) =>
      btn.addEventListener('click', drawer.close)
    );
    window.v5MenuDrawer = drawer;
  });

  /* ---------------------------------------------------------------------
     Mobile search: reveal the field, focus it.
     --------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-v5-search-open]');
    if (!btn) return;
    const host = document.querySelector('.v5-header__search');
    if (!host) return;
    const open = host.classList.toggle('is-revealed');
    btn.setAttribute('aria-expanded', String(open));
    if (open) {
      const input = host.querySelector('input[type="search"]');
      if (input) requestAnimationFrame(() => input.focus());
    }
  });

  /* ---------------------------------------------------------------------
     Cart count: pulse once when it changes, so an add-to-cart is always
     acknowledged even when the drawer is off.
     --------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', () => {
    const el = document.querySelector('[data-cart-count]');
    if (!el) return;
    let last = parseInt(el.textContent, 10) || 0;
    new MutationObserver(() => {
      const now = parseInt(el.textContent, 10) || 0;
      el.classList.toggle('is-empty', now === 0);
      if (now > last && !reduced.matches) {
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      }
      last = now;
    }).observe(el, { childList: true, characterData: true, subtree: true });
  });

  /* ---------------------------------------------------------------------
     Reveal on scroll. Opacity always; the lift is dropped under
     reduced-motion because the fade alone still explains the arrival.
     --------------------------------------------------------------------- */
  function initReveal(root) {
    const items = (root || document).querySelectorAll('.v5-reveal:not(.is-in)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        fired = true;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );
    items.forEach((el) => io.observe(el));
    /* Watchdog: content is hidden until the observer speaks, so an observer
       that never delivers (throttled tab, restored bfcache page, embedded
       frame) would leave the page permanently blank. If nothing has been
       reported by the time the animation would have finished anyway, show
       everything and stop observing. Nobody sees a fade they waited 2s for. */
    setTimeout(() => {
      if (fired) return;
      io.disconnect();
      items.forEach((el) => el.classList.add('is-in'));
    }, 2000);
  }
  document.addEventListener('DOMContentLoaded', () => initReveal());
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
  window.v5InitReveal = initReveal;
})();

/* ==========================================================================
   Drag-to-scroll for horizontal lists marked [data-v5-dragscroll].

   Touch is deliberately left alone: native inline scrolling already has the
   right momentum and rubber-banding, and re-implementing it always feels
   worse. This adds the pointer drag a mouse user otherwise has no way to
   perform, and nothing else — wheel, shift-wheel, trackpad and arrow keys are
   already handled by the scroll container itself.
   ========================================================================== */
(function () {
  'use strict';

  const THRESHOLD = 6; // px of travel before a press counts as a drag

  function bind(el) {
    if (el.dataset.v5Dragbound) return;
    el.dataset.v5Dragbound = '1';

    let pid = null, startX = 0, startScroll = 0, moved = 0;

    el.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      pid = e.pointerId;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      moved = 0;
      /* Capture keeps the drag alive when the pointer leaves the rail. If the
         browser refuses it the drag still works, just not past the edges, so
         it must not take the gesture down with it. */
      try { el.setPointerCapture(pid); } catch (err) {}
      el.classList.add('is-dragging');
    });

    el.addEventListener('pointermove', (e) => {
      if (pid === null || e.pointerId !== pid) return;
      const dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      // scrollLeft counts up rightwards in both writing modes in modern
      // browsers, so the content follows the pointer either way.
      el.scrollLeft = startScroll - dx;
    });

    const end = (e) => {
      if (pid === null || (e && e.pointerId !== pid)) return;
      try { el.releasePointerCapture(pid); } catch (err) {}
      pid = null;
      el.classList.remove('is-dragging');
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);

    /* A drag across a card must not open that card. Capture phase, so the
       card's own link never sees the click. */
    el.addEventListener('click', (e) => {
      if (moved > THRESHOLD) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    /* Native drag-and-drop would otherwise start on the product images and
       cancel the pointer stream mid-gesture. */
    el.addEventListener('dragstart', (e) => {
      if (moved > THRESHOLD) e.preventDefault();
    });

    /* Chrome scrolls a focused container with the arrow keys on its own, but
       only while the container itself holds focus — and it gives no page-step
       or jump-to-end. Handled explicitly so the rail is as reachable from the
       keyboard as it is from a pointer. Events bubbling up from a focused card
       link are left alone; tabbing through the cards already scrolls them
       into view. */
    el.addEventListener('keydown', (e) => {
      if (document.activeElement !== el) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const page = el.clientWidth * 0.85;
      // scrollLeft always grows rightwards; RTL simply runs 0 → negative
      // instead of 0 → positive. So the arrows can map straight to the
      // direction on screen, and only "forward through the list" needs to
      // know the writing direction.
      const forward = getComputedStyle(el).direction === 'rtl' ? -1 : 1;
      let dx = 0;
      switch (e.key) {
        case 'ArrowRight': dx = page * 0.4; break;
        case 'ArrowLeft':  dx = page * -0.4; break;
        case 'PageDown':   dx = forward * page; break;
        case 'PageUp':     dx = forward * -page; break;
        case 'Home':       dx = -el.scrollLeft; break;
        case 'End':        dx = forward * (el.scrollWidth - el.clientWidth) - el.scrollLeft; break;
        default: return;
      }
      /* Easing is CSS's job (scroll-behavior on the container, dropped under
         reduced-motion). But a smooth scroll that is never animated leaves the
         key doing nothing at all, so the step is checked shortly after and
         completed without easing if nothing has moved. A running animation has
         already covered ground by then, so it is left alone. */
      const before = el.scrollLeft;
      el.scrollLeft = before + dx;
      setTimeout(() => {
        if (el.scrollLeft !== before) return;
        const prev = el.style.scrollBehavior;
        el.style.scrollBehavior = 'auto';
        el.scrollLeft = before + dx;
        el.style.scrollBehavior = prev;
      }, 120);
      e.preventDefault();
    });
  }

  function init(root) {
    (root || document).querySelectorAll('[data-v5-dragscroll]').forEach(bind);
  }
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  window.v5InitDragScroll = init;
})();

/* ==========================================================================
   Product recommendations — fetched after paint.
   ========================================================================== */
(function () {
  'use strict';
  class V5Recommendations extends HTMLElement {
    connectedCallback() {
      const load = () => {
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((text) => {
            const el = new DOMParser().parseFromString(text, 'text/html').querySelector('v5-recommendations');
            if (el && el.innerHTML.trim()) this.innerHTML = el.innerHTML;
          })
          .catch(() => {});
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            io.disconnect();
            load();
          },
          { rootMargin: '600px 0px' }
        );
        io.observe(this);
      } else {
        load();
      }
    }
  }
  if (!customElements.get('v5-recommendations')) customElements.define('v5-recommendations', V5Recommendations);
})();

/* ==========================================================================
   Bidi isolation for phone numbers inside merchant rich text.

   A number written "+972 52-226-3724" inside a Hebrew paragraph renders as
   "972 52-226-3724+" because "+" is a bidi-neutral character and attaches to
   the RTL run. Wrapping the number in <bdi dir="ltr"> fixes the display
   without altering a single character of the merchant's content.
   ========================================================================== */
(function () {
  'use strict';

  // Long runs of digits and phone punctuation only. Prices (₪339.90) and time
  // ranges (09:00-17:00) fall short of the 9-digit floor applied below.
  const PHONE = /\+?\d[\d\-\s()]{7,}\d/g;
  const SCOPES = '.v5-prose, .v5-footer__rich, .v5-pagebody__content, .v5-acc__body';

  function isolate(root) {
    root.querySelectorAll(SCOPES).forEach((scope) => {
      const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!node.nodeValue || node.nodeValue.length < 9) return NodeFilter.FILTER_REJECT;
          if (node.parentElement.closest('bdi, a[href^="tel:"], code, pre')) return NodeFilter.FILTER_REJECT;
          return PHONE.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        },
      });
      const targets = [];
      let n;
      while ((n = walker.nextNode())) targets.push(n);

      targets.forEach((node) => {
        const text = node.nodeValue;
        const frag = document.createDocumentFragment();
        let last = 0;
        text.replace(PHONE, (match, index) => {
          const digits = (match.match(/\d/g) || []).length;
          if (digits < 9) return match;
          if (index > last) frag.appendChild(document.createTextNode(text.slice(last, index)));
          const bdi = document.createElement('bdi');
          bdi.setAttribute('dir', 'ltr');
          bdi.textContent = match;
          frag.appendChild(bdi);
          last = index + match.length;
          return match;
        });
        if (!frag.childNodes.length) return;
        if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
        node.parentNode.replaceChild(frag, node);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => isolate(document));
  document.addEventListener('shopify:section:load', (e) => isolate(e.target));
})();
